import { Button, message, Space } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import ReactSignatureCanvas from 'react-signature-canvas'

// react-signature-canvas uses CJS with __esModule flag, which can cause
// the default import to resolve to the module object instead of the component
const SignatureCanvas =
  'default' in ReactSignatureCanvas
    ? (
        ReactSignatureCanvas as unknown as {
          default: typeof ReactSignatureCanvas
        }
      ).default
    : ReactSignatureCanvas
import styled from 'styled-components'

const SignatureContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1rem;
  width: 25rem;
`

const CanvasContainer = styled.div<{ $disabled?: boolean }>`
  display: inline-block;
  border: 1px solid #d9d9d9;
  border-radius: 0.125rem;
  margin-bottom: 0.5rem;
  background: #fff;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

  .signature-canvas {
    touch-action: none;
  }
`

const Instructions = styled.div`
  margin-bottom: 0.5rem;
  color: #666;
  font-size: 0.75rem;
`

interface SignaturePadProps {
  onSign: (signature: string) => void
  width?: number
  height?: number
  disabled?: boolean
  initialSignature?: string
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onSign,
  width = 500,
  height = 200,
  disabled = false,
  initialSignature,
}) => {
  const sigPadRef = useRef<ReactSignatureCanvas>(null)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    if (initialSignature && sigPadRef.current) {
      const img = new Image()
      img.onload = () => {
        const canvas = sigPadRef.current?.getCanvas()
        if (canvas) {
          const ctx = canvas.getContext('2d')
          ctx?.drawImage(img, 0, 0)
          setHasSignature(true)
        }
      }
      img.src = initialSignature
    }
  }, [initialSignature])

  const handleClear = () => {
    sigPadRef.current?.clear()
    setHasSignature(false)
  }

  const handleBegin = () => {
    setHasSignature(true)
  }

  const handleSave = () => {
    if (!hasSignature || sigPadRef.current?.isEmpty()) {
      message.warning('Please provide a signature before saving')
      return
    }
    const signatureData = sigPadRef.current?.toDataURL()
    if (signatureData) {
      onSign(signatureData)
    } else {
      message.error('Failed to capture signature')
    }
  }

  return (
    <SignatureContainer>
      <Instructions>
        Use your mouse or touch screen to sign below. Click or tap and drag to
        create your signature.
      </Instructions>
      <CanvasContainer $disabled={disabled}>
        <SignatureCanvas
          ref={sigPadRef}
          canvasProps={{
            width,
            height,
            className: 'signature-canvas',
          }}
          onBegin={handleBegin}
        />
      </CanvasContainer>
      <Space>
        <Button onClick={handleClear} disabled={!hasSignature || disabled}>
          Clear
        </Button>
        <Button onClick={handleSave} disabled={!hasSignature || disabled}>
          Save Signature
        </Button>
      </Space>
    </SignatureContainer>
  )
}
